<template lang="pug">
.menu-container.transform(class="portrait:transform-none portrait:top-0 portrait:z-10")
  .menu-outer-frame.h-full
    .menu-inner-frame.flex.flex-col.h-full
      .relative.side-logo.cursor-pointer.self-center.my-3(class="portrait:xs:my-2 h-[2.56rem]", @click="clickLogo")
        img.w-full.h-full.object-contain(:src="logoUrl")
      ul.menu-list.overflow-y-auto.overflow-x-hidden
        MenuItem(v-for="item in menuList", ref="menu", :item="item", :menuItemStack="menuItemStack", :historyStack="historyStack", :key="item.categoryId", @click="select", @backward="backward")
</template>

<script setup>
import { onMounted, getCurrentInstance } from 'vue'

import MenuItem from '@/components/MenuItem.vue'
import { useMainStore } from '@/stores/main'

const props = defineProps({
  menuItemStack: Array,
  historyStack: Array,
  menuList: Array,
})

const emit = defineEmits(['clickLogo'])

const instance = getCurrentInstance()
const $analytics = instance.appContext.config.globalProperties.$analytics

const { getBackCdnUrl } = useCdnAssets()
const sideMenu = useSideMenu()
const useGameComposable = useGame()

const mainStore = useMainStore()
const { setModal } = mainStore

const logoUrl = getBackCdnUrl('icon/logo.png')

// Prefetch logo 圖片
useHead({
  link: [{ rel: 'prefetch', href: logoUrl, as: 'image' }],
})

const select = (item) => {
  if (item.isGame) {
    setModal('ModalGuestLogin')
    return
  }

  $analytics.push({
    event: 'lobby_click',
    location: 'lobby',
    icon: item.name?.['en-US'],
    iconId: item.categoryId,
  })

  if (item.parents?.length) {
    item.parents.forEach((parent) => {
      delete parent.parents
      sideMenu.selectMenuItem(parent)
    })
  }

  delete item.parents
  sideMenu.selectMenuItem(item)
  useGameComposable.setRoom(item)
}

const clickLogo = () => {
  emit('clickLogo')
}

const backward = () => {
  useGameComposable.setRoom(props.historyStack.slice(-1)[0])
  sideMenu.popHistory()
}

onMounted(() => {
  if (useGameComposable.room?.value && useGameComposable.room?.value?.categoryId) {
    useGameComposable.recoverSetRoom(useGameComposable.room.value)
  } else {
    const lastItem = props.menuItemStack.slice(-1)[0]
    useGameComposable.setRoom(lastItem)
  }
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

.menu-outer-frame {
  @apply relative border-menu-field-border bg-linear-to-b from-menu-field-from to-menu-field-to;
  border-inline-end-width: 0.2rem;
  border-inline-end-style: solid;
  inset: 0rem;
}

.menu-outer-frame::before {
  content: '';
  position: absolute;
  border-inline-end: 0.0625rem solid #ffffffcc;
  inset: 0rem;
  inset-inline-end: -0.125rem;
}

.menu-inner-frame {
  @apply relative bg-linear-to-b from-menu-bg-from to-menu-bg-to border-menu-bg-border text-menu-bg-border me-1;
  border-inline-end-width: 0.2rem;
  border-inline-end-style: solid;
}

.menu-inner-frame::before {
  content: '';
  position: absolute;
  border-inline-end: 0.0625rem solid #ffffff7f;
  inset: -0.5rem;
  inset-inline-end: -0.125rem;
}

.menu-list {
  @apply mb-18;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.menu-list ::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.menu-list::-webkit-scrollbar {
  display: none;
}

.menu-item {
  @apply relative;
  font-size: 18px;
}

.menu-item.active {
  @apply bg-linear-to-b from-menu-active-from to-menu-active-to;
}

.menu-item:not(:last-child)::before {
  content: '';
  position: absolute;
  bottom: -0.0625rem;
  inset-inline-start: 0.375rem;
  inset-inline-end: 0.375rem;
  height: 0.0625rem;
  background-color: rgba(0, 0, 0, 0.4);
}

.menu-item:not(:last-child)::after {
  @apply bg-white;
  background-color: rgba(255, 255, 255, 0.2);
  content: '';
  position: absolute;
  bottom: -0.125rem;
  inset-inline-start: 0.375rem;
  inset-inline-end: 0.375rem;
  height: 0.0625rem;
}

.menu-item button:disabled {
  filter: grayscale(1);
}

.menu-icon {
  width: 32px;
  height: 32px;
  margin-inline-end: 8px;
}

@media (min-width: 1025px) and (orientation: landscape) {
  .menu-container {
    width: 200px;
  }
}
</style>
